import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button, ButtonGroup } from '@/components/ui';
import { menuWidthBounds, DROPDOWN_OPTION_CLASS, DROPDOWN_OPTION_LABEL_CLASS } from './dropdownMenuWidth';

interface MultiSelectDropdownProps<T> {
  options: T[];
  value: (string | number)[] | undefined | null;
  onChange: (value: (string | number)[]) => void;
  formatOption: (option: T) => string;
  formatDisplay?: (option: T) => string;
  getValue: (option: T) => string | number;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  readOnly?: boolean;
  readOnlyStyle?: React.CSSProperties;
  label?: string;
  isRequired?: boolean;
  noOptionsMessage?: string;
  maxDisplayItems?: number;
  showSelectedCount?: boolean;
  /** Render options in the order given instead of re-sorting them. Use when order is meaningful. */
  preserveOptionsOrder?: boolean;
  /** Show the in-dropdown search box. Turn off for short fixed option sets (e.g. two choices). */
  showSearch?: boolean;
  /**
   * Optional grouping: options are listed under a heading per group (e.g. foreman areas under their
   * service area, so two areas with the same name can be told apart). The search also matches the
   * group name, and a heading selects or clears its whole group. Options without a group (empty
   * string) are listed first, without a heading.
   */
  groupBy?: (option: T) => string;
  /** Order of the group headings. Defaults to natural order ("Service Area 9" before "Service Area 16"). */
  compareGroups?: (a: string, b: string) => number;
}

const naturalCompare = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export function MultiSelectDropdown<T>({
  options,
  value = [],
  onChange,
  formatOption,
  formatDisplay,
  getValue,
  placeholder = 'Select options',
  disabled = false,
  required = false,
  className = '',
  style = {},
  readOnly = false,
  readOnlyStyle = {},
  label,
  isRequired = false,
  noOptionsMessage = 'No options available',
  maxDisplayItems = 3,
  showSelectedCount = true,
  preserveOptionsOrder = false,
  showSearch = true,
  groupBy,
  compareGroups = naturalCompare
}: MultiSelectDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number; maxWidth?: number; maxHeight: number; openUp?: boolean } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuPortalRef = useRef<HTMLDivElement>(null); // used for click-outside when menu is portaled to body
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Ensure value is always an array
  const selectedValues = Array.isArray(value) ? value : [];

  const normalizeValue = (v: string | number): string => String(v).trim();
  const valuesMatch = (a: string | number, b: string | number): boolean =>
    normalizeValue(a) === normalizeValue(b);
  const isValueSelected = (optionValue: string | number): boolean =>
    selectedValues.some((val) => valuesMatch(val, optionValue));

  // Filter options based on search query
  const query = searchQuery.toLowerCase();
  const matchingOptions = options.filter(option =>
    formatOption(option).toLowerCase().includes(query) ||
    (groupBy ? groupBy(option).toLowerCase().includes(query) : false)
  );

  // Sorting here is a convenience for unordered lists, but it silently overrides callers whose
  // order is meaningful — task statuses must read Proposed -> Planned -> Active -> ... , not
  // alphabetically. `preserveOptionsOrder` opts out, matching SearchableDropdown's prop of the
  // same name so the two dropdowns behave alike.
  const filteredOptions = preserveOptionsOrder
    ? matchingOptions
    : [...matchingOptions].sort((a, b) => {
        // Get the display text for both options
        const aText = formatOption(a);
        const bText = formatOption(b);

        // Try to parse as numbers first
        const aNum = parseFloat(aText);
        const bNum = parseFloat(bText);

        // If both are valid numbers, sort numerically in ascending order
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }

        // If either is not a number, sort as strings in ascending order (lexicographically)
        return aText.localeCompare(bText);
      });

  // Grouped view: one heading per group, the options keep the order worked out above.
  const groups: { label: string; options: T[] }[] | null = groupBy
    ? (() => {
        const map = new Map<string, T[]>();
        filteredOptions.forEach((option) => {
          const label = groupBy(option) || '';
          const list = map.get(label) ?? [];
          list.push(option);
          map.set(label, list);
        });
        const labels = Array.from(map.keys());
        const ordered = preserveOptionsOrder ? labels : [...labels].sort((a, b) => (a === '' ? -1 : b === '' ? 1 : compareGroups(a, b)));
        return ordered.map((label) => ({ label, options: map.get(label) ?? [] }));
      })()
    : null;

  // Find the selected options
  const selectedOptions = options.filter((option) => isValueSelected(getValue(option)));

  const updateMenuPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const padding = 16;
      const defaultMaxHeight = 380;
      const minHeight = 240;

      // Prefer modal content bounds so the dropdown stays within the modal window
      const modalContent = triggerRef.current.closest('.modal-content');
      let containerTop: number;
      let containerBottom: number;
      if (modalContent) {
        const containerRect = modalContent.getBoundingClientRect();
        containerTop = containerRect.top;
        containerBottom = containerRect.bottom;
      } else if (typeof window !== 'undefined') {
        containerTop = 0;
        containerBottom = window.innerHeight;
      } else {
        setMenuPosition({ top: rect.bottom, left: rect.left, width: rect.width, maxHeight: defaultMaxHeight });
        return;
      }

      const availableBelow = containerBottom - rect.bottom - padding;
      const availableAbove = rect.top - containerTop - padding;

      // Open upward only when there is enough usable space above.
      // This avoids placing the menu outside the visible modal viewport.
      const canOpenUp = availableAbove >= minHeight;
      const canOpenDown = availableBelow >= minHeight;
      const openUp = canOpenUp && (!canOpenDown || availableAbove > availableBelow);
      const space = openUp ? availableAbove : availableBelow;
      const maxHeight = Math.min(defaultMaxHeight, Math.max(minHeight, space));

      const bounds = menuWidthBounds(rect.left, rect.width, window.innerWidth);
      if (openUp) {
        setMenuPosition({
          left: bounds.left,
          width: bounds.minWidth,
          maxWidth: bounds.maxWidth,
          maxHeight,
          openUp: true,
          top: rect.top - maxHeight,
        });
      } else {
        setMenuPosition({ top: rect.bottom, left: bounds.left, width: bounds.minWidth, maxWidth: bounds.maxWidth, maxHeight });
      }
    }
  };

  useLayoutEffect(() => {
    if (isOpen) {
      updateMenuPosition();
    } else {
      setMenuPosition(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen]);

  // Handle click outside to close dropdown (trigger and portaled menu)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = dropdownRef.current?.contains(target);
      const inMenu = menuPortalRef.current?.contains(target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled && !readOnly) {
      setIsOpen(!isOpen);
      setSearchQuery('');
    }
  };

  const handleOptionToggle = (option: T) => {
    const optionValue = getValue(option);
    const newValues = isValueSelected(optionValue)
      ? selectedValues.filter((v) => !valuesMatch(v, optionValue))
      : [...selectedValues, normalizeValue(optionValue)];

    onChange(newValues);
  };

  // `filteredOptions` is narrowed by the search box, and this used to REPLACE the selection with
  // it. Typing "asph", then clicking Select All, silently dropped every already-selected option
  // that did not match "asph" - with no feedback, and no way to tell it had happened. Union with
  // the existing selection instead, so Select All only ever adds.
  //
  // Values are normalized on the way in to match handleOptionToggle, which stores
  // normalizeValue(...) strings; this used to push raw getValue() results, so the same logical
  // selection could be held in two different shapes depending on how it was made.
  const handleSelectAll = () => {
    const merged = new Set(selectedValues.map(normalizeValue));
    filteredOptions.forEach((option) => merged.add(normalizeValue(getValue(option))));
    onChange(Array.from(merged));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  /** A group heading adds the whole group, or clears it when it is all selected already. */
  const handleGroupToggle = (groupOptions: T[]) => {
    const values = groupOptions.map((option) => normalizeValue(getValue(option)));
    const allSelected = values.every((v) => isValueSelected(v));
    if (allSelected) {
      onChange(selectedValues.filter((v) => !values.includes(normalizeValue(v))));
    } else {
      const merged = new Set(selectedValues.map(normalizeValue));
      values.forEach((v) => merged.add(v));
      onChange(Array.from(merged));
    }
  };

  const renderOption = (option: T, key: React.Key) => {
    const optionValue = getValue(option);
    const isSelected = isValueSelected(optionValue);
    const optionLabel = formatOption(option);
    return (
      <button
        key={key}
        type="button"
        aria-pressed={isSelected}
        title={optionLabel}
        className={`${DROPDOWN_OPTION_CLASS} ${isSelected ? 'tw-bg-primary tw-text-white' : 'hover:tw-bg-surface-hover'}`}
        onClick={() => handleOptionToggle(option)}
        style={{
          padding: '0.5rem 1rem',
          borderLeft: isSelected ? '4px solid var(--bs-primary)' : '4px solid transparent'
        }}
      >
        {/* The top margin puts the box on the first text line's centre. */}
        <input
          type="checkbox"
          className="tw-mt-[0.2rem] tw-shrink-0"
          checked={isSelected}
          onChange={() => {}}
          onClick={(e) => e.stopPropagation()}
          style={{ pointerEvents: 'none' }}
        />
        <span className={DROPDOWN_OPTION_LABEL_CLASS}>{optionLabel}</span>
        {isSelected && <i className="bi bi-check2 tw-shrink-0" aria-hidden="true" />}
      </button>
    );
  };

  const handleClearFilter = () => {
    onChange([]);
    setSearchQuery('');
  };

  const renderLabel = () => {
    if (!label) return null;
    return (
      <label className="tw-mb-2">
        {label} {isRequired && <span className="tw-text-danger">*</span>}
      </label>
    );
  };

  const renderDisplayValue = () => {
    if (selectedOptions.length === 0) {
      return placeholder;
    }

    if (selectedOptions.length <= maxDisplayItems) {
      return selectedOptions.map(option => 
        formatDisplay ? formatDisplay(option) : formatOption(option)
      ).join(', ');
    }

    if (showSelectedCount) {
      return `${selectedOptions.length} item(s) selected`;
    }

    return selectedOptions.slice(0, maxDisplayItems).map(option => 
      formatDisplay ? formatDisplay(option) : formatOption(option)
    ).join(', ') + ` +${selectedOptions.length - maxDisplayItems} more`;
  };

  // Portal menu to document.body so it is not clipped by overflow/scroll containers
  const dropdownMenuContent = isOpen && !readOnly && menuPosition && (
    <div
      ref={menuPortalRef}
      data-testid="multiselect-menu"
      className="tw-bg-surface tw-border tw-border-line tw-rounded tw-shadow-sm tw-text-left"
      style={{
        position: 'fixed',
        top: menuPosition.top,
        left: menuPosition.left,
        // At least the field's width, growing to the longest option up to the shared cap.
        width: 'max-content',
        minWidth: menuPosition.width,
        maxWidth: menuPosition.maxWidth ?? menuPosition.width,
        zIndex: 3000,
        maxHeight: `${menuPosition.maxHeight}px`,
        overflow: 'hidden',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="tw-px-2 tw-py-2 tw-border-b tw-border-line tw-sticky tw-top-0 tw-bg-surface" style={{ flexShrink: 0 }}>
        {/* Search row — hidden for short fixed option sets where a search box is only noise.
            Select All / Clear All below stay available either way. */}
        {showSearch && (
        <div className="tw-flex tw-grow">
          <span className="tw-flex tw-items-center tw-px-2 tw-border tw-border-line-input tw-bg-surface-alt">
            <i className="bi bi-search" />
          </span>
          <input
            ref={searchInputRef}
            type="text"
            className="tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-1.5 focus:tw-shadow-focus"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          {searchQuery && (
            <Button
              variant="outline-secondary"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSearchQuery('');
              }}
            >
              <i className="bi bi-x" />
            </Button>
          )}
        </div>
        )}

        {/* Action buttons */}
        <div className={`tw-flex tw-justify-between tw-items-center tw-gap-2 tw-text-left ${showSearch ? 'tw-mt-2' : ''}`}>
          <ButtonGroup size="sm">
            <Button
              variant="outline-primary"
              size="sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAll();
              }}
              title="Select All"
            >
              Select All
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              title="Clear All"
            >
              Clear All
            </Button>
          </ButtonGroup>
          <Button
            variant="link"
            size="sm"
            className="tw-text-content-muted"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClearFilter();
            }}
            title="Remove Filter"
            style={{ padding: '4px' }}
          >
            <i className="bi bi-eraser" />
          </Button>
        </div>
      </div>

      {/* Options list */}
      <div className="options-container tw-py-1" style={{ overflowY: 'auto', overflowX: 'hidden', flex: '1 1 auto', minHeight: 0 }}>
        {filteredOptions.length === 0 ? (
          <div className="tw-px-4 tw-py-1 tw-text-content-muted">
            {noOptionsMessage}
          </div>
        ) : groups ? (
          groups.map((group) => (
            <div key={group.label || '__none'} role="group" aria-label={group.label || undefined}>
              {group.label && (
                <button
                  type="button"
                  data-testid="multiselect-group"
                  className="tw-block tw-w-full tw-border-0 tw-bg-surface-alt tw-px-4 tw-py-1 tw-text-left tw-text-xs tw-font-semibold tw-text-content-secondary hover:tw-bg-surface-hover"
                  title={`Select or clear every option in ${group.label}`}
                  onClick={() => handleGroupToggle(group.options)}
                >
                  {group.label}
                </button>
              )}
              {group.options.map((option) => renderOption(option, String(getValue(option))))}
            </div>
          ))
        ) : (
          filteredOptions.map((option, index) => renderOption(option, index))
        )}
      </div>
    </div>
  );

  return (
    <div className="multiselect-dropdown" ref={dropdownRef}>
      {renderLabel()}
      <div className="tw-relative">
        {/* Main input with integrated arrow */}
        <div className="tw-relative" ref={triggerRef}>
          <input
            type="text"
            className={`tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-1.5 focus:tw-shadow-focus ${className}`}
            value={renderDisplayValue()}
            placeholder={placeholder}
            onClick={handleToggle}
            readOnly
            disabled={disabled}
            style={{
              cursor: disabled || readOnly ? 'not-allowed' : 'pointer',
              paddingRight: '30px',
              ...(readOnly ? readOnlyStyle : {}),
              ...style
            }}
          />
          {!readOnly && (
            <i
              className={`bi bi-chevron-${isOpen ? 'up' : 'down'} tw-absolute`}
              style={{ 
                right: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6c757d'
              }}
            />
          )}
        </div>
      </div>
      {dropdownMenuContent && createPortal(dropdownMenuContent, document.body)}
    </div>
  );
} 